import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PrinterIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import { Contract } from '../../contexts/RentalContext';
import { getContractTemplate } from '../../utils/contractTemplates';
import { useAuth } from '../../contexts/AuthContext';
interface ContractPreviewModalProps {
  contract: Contract | any; // Accepting any to support formData from wizard
  isOpen: boolean;
  onClose: () => void;
  title?: string;
}
export function ContractPreviewModal({
  contract,
  isOpen,
  onClose,
  title = 'Previzualizare Contract'
}: ContractPreviewModalProps) {
  const {
    user
  } = useAuth();
  const agentName = user?.fullName || 'Agent';
  const contractText = getContractTemplate(contract, agentName);
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const styles = `
        body { 
          font-family: 'Times New Roman', serif; 
          font-size: 12pt; 
          line-height: 1.6; 
          padding: 40px; 
        }
        .header { text-align: center; margin-bottom: 30px; }
        .section { margin-bottom: 20px; }
        .signatures { margin-top: 50px; display: flex; justify-content: space-between; }
        .signature-box { width: 45%; }
        @media print {
          body { padding: 0; }
        }
      `;
      const htmlContent = `
        <html>
          <head>
            <title>Contract - ${contract.id || 'Draft'}</title>
            <style>{`;
      $;
      {
        styles;
      }
      ;
      `}</style>
          </head>
          <body>
            <div style="white-space: pre-wrap;">${contractText}</div>
          </body>
        </html>
      `;
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };
  return <AnimatePresence data-id="element-3404">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-3405" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none" data-id="element-3406">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col pointer-events-auto overflow-hidden border border-slate-200" data-id="element-3407">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-b border-slate-200" data-id="element-3408">
                <div className="flex items-center space-x-3" data-id="element-3409">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600" data-id="element-3410">
                    <FileTextIcon className="w-5 h-5" data-id="element-3411" />
                  </div>
                  <div data-id="element-3412">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-3413">
                      {title}
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-3414">
                      {contract.id ? `ID: ${contract.id}` : 'Draft neînregistrat'}{' '}
                      • {new Date().toLocaleDateString('ro-RO')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2" data-id="element-3415">
                  <button onClick={handlePrint} className="p-2 text-slate-600 hover:bg-white hover:text-blue-600 rounded-lg transition-colors border border-transparent hover:border-slate-200 hover:shadow-sm" title="Printează" data-id="element-3416">
                    <PrinterIcon className="w-5 h-5" data-id="element-3417" />
                  </button>
                  <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-3418">
                    <XIcon className="w-6 h-6" data-id="element-3419" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-8 bg-slate-100" data-id="element-3420">
                <div className="max-w-[210mm] mx-auto bg-white shadow-lg p-[20mm] min-h-full" data-id="element-3421">
                  <pre className="whitespace-pre-wrap font-serif text-slate-900 text-sm sm:text-base leading-relaxed" data-id="element-3422">
                    {contractText}
                  </pre>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-white border-t border-slate-200 flex justify-end space-x-3" data-id="element-3423">
                <button onClick={onClose} className="px-4 py-2 text-slate-600 font-semibold hover:bg-slate-50 rounded-lg transition-colors" data-id="element-3424">
                  Închide
                </button>
                <button onClick={handlePrint} className="flex items-center space-x-2 px-4 py-2 bg-[#357BEB] text-white font-semibold rounded-lg hover:bg-[#2868d8] transition-colors shadow-sm hover:shadow" data-id="element-3425">
                  <PrinterIcon className="w-4 h-4" data-id="element-3426" />
                  <span data-id="element-3427">Printează / PDF</span>
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}